/**
 * Desktop host: QR code, session code, WebSocket relay integration.
 */
import QRCode from "qrcode";
import { WsClient, type WsConnectionStatus, type WsMessage } from "./ws-client";
import {
  clientLink,
  isViewerReport,
  type ClientPresence,
  type ViewerReport,
} from "../lib/beeldbellen-status";
import { shareableHref } from "../lib/dev-share-host";

const CODE_CHARS = "BCDFGHJKLMNPQRSTVWXYZ2346789";
const SESSION_KEY = "emdr-remote-code";
const VIEWER_KEY = "emdr-viewer-code";
const PREVIEW_CODE = "DEMO12";

export const BEELDBELLEN_EVENT = "emdr-beeldbellen-change";

let client: WsClient | null = null;
let activeCode: string | null = null;
let remoteConnected = false;

/** Beeldbellen: kijkcode voor de cliënt en wat de cliëntweergave terugmeldt. */
let viewerCode: string | null = null;
let viewerCount = 0;
let viewerEverJoined = false;
let viewerReport: ViewerReport | null = null;
/** Heeft de behandelaar in deze sessie beeldbellen gestart (of is er al een cliënt)? */
let beeldbellenStarted = false;
let hostStatus: WsConnectionStatus = "idle";
/** Taakinstellingen bij de laatste verzending; een wijziging vraagt een nieuwe seed. */
let lastTaskSignature = "";

function $(id: string): HTMLElement | null {
  return document.getElementById(id);
}

export function generateSessionCode(): string {
  let code = "";
  const bytes = crypto.getRandomValues(new Uint8Array(6));
  for (let i = 0; i < 6; i++) {
    code += CODE_CHARS[bytes[i] % CODE_CHARS.length];
  }
  return code;
}

export function remoteLink(code: string): string {
  const url = new URL(shareableHref());
  url.searchParams.set("remote", code);
  url.searchParams.delete("token");
  return url.toString();
}

function setStatus(status: WsConnectionStatus, attempt = 0): void {
  const pill = $("remote-status-pill");
  const text = $("remote-status-text");
  if (!pill || !text) return;

  pill.hidden = false;
  pill.className = `remote-status-pill remote-status-pill--${status}`;

  if (status === "connecting") {
    text.textContent = "Verbinding maken…";
    return;
  }
  if (status === "reconnecting") {
    text.textContent = `Verbinding verbroken. Probeert opnieuw (poging ${attempt})…`;
    return;
  }
  if (status === "closed") {
    text.textContent = "Verbinding gesloten";
    return;
  }
  if (remoteConnected) {
    text.textContent = "Mobiel verbonden";
    return;
  }
  text.textContent = activeCode ? "Wachten op mobiel…" : "Niet verbonden";
}

function updateRemoteUi(): void {
  const qr = $("remote-qr");
  const linkEl = $("remote-link");
  const codeEl = $("remote-code");
  const startBtn = $("btn-start-remote");
  const stopBtn = $("btn-stop-remote");
  const sessionActive = !!activeCode;

  if (qr && !qr.classList.contains("remote-qr-wrap--preview")) {
    qr.hidden = !sessionActive;
  }
  if (linkEl) linkEl.hidden = !sessionActive;
  if (codeEl) codeEl.hidden = !sessionActive;
  if (startBtn) startBtn.hidden = sessionActive;
  if (stopBtn) stopBtn.hidden = !remoteConnected;
}

async function renderQrCanvas(
  qrEl: HTMLElement,
  code: string,
  options?: { preview?: boolean }
): Promise<void> {
  qrEl.innerHTML = "";
  qrEl.classList.toggle("remote-qr-wrap--preview", !!options?.preview);
  const canvas = document.createElement("canvas");
  if (options?.preview) {
    canvas.setAttribute("aria-hidden", "true");
    qrEl.setAttribute(
      "aria-label",
      "Voorbeeld van QR-code voor mobiele bediening (alleen beschikbaar in de betaalde versie)"
    );
  } else {
    qrEl.removeAttribute("aria-label");
    canvas.setAttribute("aria-label", `QR-code voor sessie ${code}`);
  }
  qrEl.appendChild(canvas);
  await QRCode.toCanvas(canvas, remoteLink(code), {
    width: 180,
    margin: 2,
    color: { dark: "#1a3a38", light: "#ffffff" },
  });
}

async function renderQr(code: string): Promise<void> {
  const qrEl = $("remote-qr");
  const linkEl = $("remote-link");
  const codeEl = $("remote-code");
  if (!qrEl) return;

  const url = remoteLink(code);
  if (codeEl) codeEl.textContent = code;
  if (linkEl) linkEl.textContent = url;
  await renderQrCanvas(qrEl, code);
}

function isBedienTabActive(): boolean {
  const panel = $("tab-panel-bediening");
  return panel ? !panel.hidden : false;
}

function hidePreviewQr(): void {
  const qrEl = $("remote-qr");
  if (!qrEl || activeCode) return;
  qrEl.classList.remove("remote-qr-wrap--preview");
  qrEl.removeAttribute("aria-label");
  qrEl.hidden = true;
  qrEl.innerHTML = "";
}

async function showPreviewQr(): Promise<void> {
  if (window.__EMDR_IS_PAID__ || activeCode || !isBedienTabActive()) return;
  const qrEl = $("remote-qr");
  if (!qrEl) return;
  qrEl.hidden = false;
  await renderQrCanvas(qrEl, PREVIEW_CODE, { preview: true });
}

function updateBedienPreview(): void {
  if (window.__EMDR_IS_PAID__ || activeCode) {
    hidePreviewQr();
    return;
  }
  if (isBedienTabActive()) void showPreviewQr();
  else hidePreviewQr();
}

function notifyBeeldbellen(): void {
  window.__EMDR_MUTED__ = viewerCount > 0;
  // Zolang een cliënt meekijkt, toont het stimulatieveld wat de cliënt ziet.
  $("stage-client-banner")?.toggleAttribute("hidden", viewerCount === 0);
  window.dispatchEvent(new CustomEvent(BEELDBELLEN_EVENT));
}

/** Alles wat bepaalt wélke taak er loopt; bal-instellingen als snelheid horen er niet bij. */
function taskSignature(data: Record<string, unknown>): string {
  const { workload, workloadKinderen, danspas, racebaan, experimental, audience, kinderenBal, stimulus } =
    data;
  return JSON.stringify({
    workload,
    workloadKinderen,
    danspas,
    racebaan,
    experimental,
    audience,
    kinderenBal,
    stimulus,
  });
}

/** Seed en fase voor de cliëntweergave; alleen zinvol terwijl de bal loopt. */
function playSync(): Pick<WsMessage, "seed" | "phase"> {
  if (viewerCount === 0 || !window.__EMDR_GET_PLAYING__?.()) return {};
  const sync = window.__EMDR_GET_SYNC__?.();
  if (!sync) return {};
  // Een tabblad op de achtergrond tekent niet; de fase is dan niet bruikbaar.
  return document.visibilityState === "visible"
    ? { seed: sync.seed, phase: sync.phase }
    : { seed: sync.seed };
}

/**
 * Verandert de taak terwijl de bal loopt, dan beginnen beide schermen de taken
 * opnieuw op dezelfde seed. Zo blijft wat de behandelaar ziet gelijk aan de cliënt.
 */
function resyncIfTasksChanged(data: Record<string, unknown>): Pick<WsMessage, "seed"> {
  const signature = taskSignature(data);
  const changed = signature !== lastTaskSignature;
  lastTaskSignature = signature;
  if (!changed || viewerCount === 0 || !window.__EMDR_GET_PLAYING__?.()) return {};
  const sync = window.__EMDR_RESYNC__?.();
  return sync ? { seed: sync.seed } : {};
}

function broadcastSync(): void {
  if (!client || client.getStatus() !== "connected") return;
  window.__EMDR_READ_STATE__?.();
  const data = window.__EMDR_GET_STATE__?.() ?? {};
  const playing = window.__EMDR_GET_PLAYING__?.() ?? false;
  const isPaid = window.__EMDR_IS_PAID__ ?? false;
  const fullscreen = window.__EMDR_GET_FULLSCREEN__?.() ?? false;
  const sud = window.__EMDR_GET_SUD__?.() ?? false;
  lastTaskSignature = taskSignature(data);
  // Een cliënt die halverwege aanhaakt krijgt een verse seed, anders lopen de taken uiteen.
  if (viewerCount > 0 && playing) window.__EMDR_RESYNC__?.();
  client.send({
    type: "sync",
    data,
    playing,
    isPaid,
    fullscreen,
    sud,
    viewerLink: getClientLink() ?? undefined,
    ...playSync(),
  });
}

function broadcastState(to?: "viewers"): void {
  if (!client || client.getStatus() !== "connected") return;
  const data = window.__EMDR_GET_STATE__?.() ?? {};
  client.send({ type: "state", data, to, ...resyncIfTasksChanged(data) });
}

function broadcastPlay(): void {
  if (!client || client.getStatus() !== "connected") return;
  client.send({
    type: "play",
    playing: window.__EMDR_GET_PLAYING__?.() ?? false,
    ...playSync(),
  });
}

function broadcastSud(): void {
  if (!client || client.getStatus() !== "connected") return;
  client.send({ type: "sud", open: window.__EMDR_GET_SUD__?.() ?? false });
}

function broadcastFullscreen(): void {
  if (!client || client.getStatus() !== "connected") return;
  client.send({
    type: "fullscreen",
    fullscreen: window.__EMDR_GET_FULLSCREEN__?.() ?? false,
  });
}

function switchSettingsTab(panelId: string): void {
  const tabs = document.querySelectorAll(".settings-tab");
  const panels = document.querySelectorAll(".tab-panel");
  tabs.forEach((tab) => {
    const isTarget = (tab as HTMLElement).dataset.tabPanel === panelId;
    tab.classList.toggle("is-active", isTarget);
    tab.setAttribute("aria-selected", isTarget ? "true" : "false");
  });
  panels.forEach((panel) => {
    panel.hidden = panel.id !== panelId;
  });
}

function setOtherTabsLocked(locked: boolean): void {
  const standardTab = $("tab-btn-standard");
  if (standardTab instanceof HTMLButtonElement) standardTab.disabled = locked;
  if (locked) switchSettingsTab("tab-panel-bediening");
}

function setDesktopControlsLocked(locked: boolean): void {
  window.__EMDR_DESKTOP_LOCKED__ = locked;
  window.dispatchEvent(new CustomEvent("emdr-remote-lock-change", { detail: { locked } }));

  const drawer = $("settings-drawer");
  const toolbar = document.querySelector(".stage-toolbar");
  const banner = $("remote-lock-banner");

  drawer?.classList.toggle("settings-remote-locked", locked);
  toolbar?.classList.toggle("settings-remote-locked", locked);
  if (banner) banner.hidden = !locked;
  setOtherTabsLocked(locked);
  notifyBeeldbellen();

  if (locked) {
    window.__EMDR_BROADCAST_STATE__ = undefined;
  } else if (activeCode && client?.getStatus() === "connected") {
    window.__EMDR_BROADCAST_STATE__ = () => broadcastState();
  }
}

function guardRemotePaid(): boolean {
  if (window.__EMDR_IS_PAID__) return true;
  window.__EMDR_ON_PAYWALL__?.("mobiele bediening", "license");
  return false;
}

function startSession(code?: string): void {
  if (!guardRemotePaid()) return;

  remoteConnected = false;
  const sessionCode = (code || generateSessionCode()).toUpperCase();
  activeCode = sessionCode;
  if (!viewerCode || viewerCode === sessionCode) viewerCode = generateSessionCode();
  viewerCount = 0;
  viewerEverJoined = false;
  viewerReport = null;
  try {
    sessionStorage.setItem(SESSION_KEY, sessionCode);
    sessionStorage.setItem(VIEWER_KEY, viewerCode);
  } catch {
    /* ignore */
  }

  updateRemoteUi();
  void renderQr(sessionCode);
  setStatus("connecting");

  client?.disconnect();
  client = new WsClient();
  client.onStatus((status, attempt) => {
    hostStatus = status;
    setStatus(status, attempt);
    notifyBeeldbellen();
  });
  client.on("request-sync", () => broadcastSync());
  const onViewerPresence = (msg: WsMessage) => {
    viewerCount = typeof msg.count === "number" ? msg.count : 0;
    if (viewerCount > 0) viewerEverJoined = true;
    else viewerReport = null;
    notifyBeeldbellen();
  };
  client.on("viewer-joined", onViewerPresence);
  client.on("viewer-left", onViewerPresence);
  client.on("viewer-status", (msg) => {
    if (!isViewerReport(msg.data)) return;
    viewerReport = msg.data;
    notifyBeeldbellen();
  });
  client.on("remote-joined", () => {
    remoteConnected = true;
    setDesktopControlsLocked(true);
    setStatus("connected");
    updateRemoteUi();
  });
  client.on("remote-left", () => {
    remoteConnected = false;
    setDesktopControlsLocked(false);
    setStatus("connected");
    const text = $("remote-status-text");
    if (text) text.textContent = "Wachten op mobiel…";
    updateRemoteUi();
  });
  client.on("state", (msg) => {
    if (msg.data && typeof msg.data === "object") {
      window.__EMDR_APPLY_REMOTE_STATE__?.(msg.data);
      // De bediening praat alleen met dit scherm; de cliëntweergave krijgt het van hier.
      if (viewerCount > 0) broadcastState("viewers");
    }
  });
  client.on("play", (msg) => {
    if (msg.playing === false) {
      // Pauze vanaf de bediening: met de SUD-schaal erbij als die aan staat.
      window.__EMDR_PAUSE__?.();
    } else if (typeof msg.playing === "boolean") {
      window.__EMDR_APPLY_REMOTE_PLAY__?.(msg.playing, { seed: msg.seed });
    }
  });
  client.on("fullscreen", (msg) => {
    if (typeof msg.fullscreen === "boolean") {
      window.__EMDR_SET_FULLSCREEN__?.(msg.fullscreen);
    }
  });
  client.on("session-end", () => {
    window.__EMDR_SET_PLAYING__?.(false);
    // Bij beeldbellen is hiermee ook de cliëntlink afgelopen; in de kamer blijft de sessie bruikbaar.
    if (beeldbellenStarted || viewerEverJoined) stopSession();
  });
  client.on("remote-exit", () => {
    remoteConnected = false;
    setDesktopControlsLocked(false);
    const text = $("remote-status-text");
    if (text) text.textContent = "Mobiel heeft verbinding gesloten";
    pillFlash("remote-exit");
    updateRemoteUi();
  });

  client.connect(sessionCode, "host", undefined, { viewer: viewerCode });

  window.__EMDR_SYNC_ACTIVE__ = () => viewerCount > 0;
  window.__EMDR_BROADCAST_STATE__ = () => broadcastState();
  window.__EMDR_BROADCAST_PLAY__ = broadcastPlay;
  window.__EMDR_BROADCAST_SUD__ = broadcastSud;
  window.__EMDR_ON_FULLSCREEN_CHANGE__ = broadcastFullscreen;
}

function pillFlash(kind: string): void {
  const pill = $("remote-status-pill");
  if (!pill) return;
  pill.classList.add(`remote-status-pill--${kind}`);
}

function ensureRemoteSession(): void {
  if (activeCode) return;
  if (window.__EMDR_IS_PAID__) startSession();
  else {
    void showPreviewQr();
    updateRemoteUi();
  }
}

export function enforceRemotePaidAccess(): void {
  if (!window.__EMDR_IS_PAID__ && activeCode) stopSession();
  else updateBedienPreview();
}

function stopSession(): void {
  client?.disconnect();
  client = null;
  activeCode = null;
  remoteConnected = false;
  viewerCode = null;
  viewerCount = 0;
  viewerEverJoined = false;
  viewerReport = null;
  beeldbellenStarted = false;
  hostStatus = "idle";
  window.__EMDR_SYNC_ACTIVE__ = undefined;
  notifyBeeldbellen();
  setDesktopControlsLocked(false);
  try {
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(VIEWER_KEY);
  } catch {
    /* ignore */
  }
  updateRemoteUi();
  setStatus("idle");
  const codeEl = $("remote-code");
  if (codeEl) codeEl.textContent = "";
  const linkEl = $("remote-link");
  if (linkEl) linkEl.textContent = "";
  window.__EMDR_BROADCAST_STATE__ = undefined;
  window.__EMDR_BROADCAST_PLAY__ = undefined;
  window.__EMDR_BROADCAST_SUD__ = undefined;
  window.__EMDR_ON_FULLSCREEN_CHANGE__ = undefined;
  updateBedienPreview();
}

export function initRemoteHost(): void {
  const startBtn = $("btn-start-remote");
  const stopBtn = $("btn-stop-remote");

  startBtn?.addEventListener("click", () => startSession());
  stopBtn?.addEventListener("click", () => stopSession());

  $("tab-btn-bediening")?.addEventListener("click", () => {
    ensureRemoteSession();
  });

  document.querySelectorAll(".settings-tab:not(#tab-btn-bediening)").forEach((tab) => {
    tab.addEventListener("click", () => hidePreviewQr());
  });

  window.__EMDR_ENFORCE_REMOTE_PAID__ = enforceRemotePaidAccess;

  try {
    const saved = sessionStorage.getItem(SESSION_KEY);
    const savedViewer = sessionStorage.getItem(VIEWER_KEY);
    if (savedViewer && /^[A-Z0-9]{6}$/.test(savedViewer)) viewerCode = savedViewer;
    if (window.__EMDR_IS_PAID__ && saved && /^[A-Z0-9]{6}$/.test(saved)) {
      startSession(saved);
    }
  } catch {
    /* ignore */
  }

  updateRemoteUi();
}

/* ── Beeldbellen: cliëntlink en status voor de begeleide opzet ── */

export type BeeldbellenState = ClientPresence & {
  /** Loopt er een sessie waar een cliënt op kan aanhaken? */
  active: boolean;
  /** Is beeldbellen in deze sessie gestart? Dan blijft de statusbalk in beeld. */
  started: boolean;
  /** De eigen verbinding van dit scherm met de server. */
  hostStatus: WsConnectionStatus;
  clientLink: string | null;
  remoteLink: string | null;
  remoteConnected: boolean;
};

function getClientLink(): string | null {
  return activeCode && viewerCode ? clientLink(shareableHref(), viewerCode) : null;
}

export function getBeeldbellenState(): BeeldbellenState {
  return {
    active: !!activeCode,
    started: beeldbellenStarted || viewerEverJoined,
    hostStatus,
    clientLink: getClientLink(),
    remoteLink: activeCode ? remoteLink(activeCode) : null,
    remoteConnected,
    count: viewerCount,
    everJoined: viewerEverJoined,
    report: viewerReport,
  };
}

/** Start de sessie als die er nog niet is; geeft false terug zonder licentie. */
export function ensureBeeldbellenSession(): boolean {
  if (!activeCode) startSession();
  beeldbellenStarted = !!activeCode;
  notifyBeeldbellen();
  return !!activeCode;
}

/** Sluit beeldbellen af: de bal stopt en de cliënt ziet dat de sessie is afgelopen. */
export function stopBeeldbellenSession(): void {
  window.__EMDR_SET_PLAYING__?.(false);
  client?.send({ type: "session-end", to: "viewers" });
  stopSession();
}
